<template>
  <v-dialog
    persistent
    eager
    v-model="value"
    max-width="600px"
  >
    <v-card class="pb-2">
      <v-card-title
        primary-title
        class="pl-0"
      >
        <h2
          class="modal-title unmatched-modal-title pl-3 py-2 roboto-font font-weight-medium"
        >
          {{ isEdit ? 'Edit Bank Account' : 'Create Bank Account' }}
        </h2>
      </v-card-title>
      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-container
            fluid
            class="mt-4"
          >
            <v-row>
              <v-col class="ma-0 pa-0">
                <h3
                  v-if="createGBP || createEUR"
                  class="unmatched-modal-title py-2 roboto-font font-weight-light"
                >
                  Choose a currency:
                </h3>
                <v-radio-group
                  class="ma-0 pa-0"
                  v-if="emptyAccounts && !isEdit"
                  v-model="currency"
                  mandatory
                >
                  <v-radio
                    v-for="c in currencies"
                    :key="c"
                    :label="c"
                    :value="c"
                  />
                </v-radio-group>
                <v-text-field
                  v-else
                  class="acc-currency-input"
                  readonly
                  hide-details
                  label="Currency"
                  v-model="getCurrency"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                class="ml-0 pl-0"
                v-if="canShow(ibanFormat)"
              >
                <v-text-field
                  label="IBAN"
                  v-model="iban"
                  :rules="ibanRules"
                  required
                />
                <v-text-field
                  label="BIC"
                  v-model="bic"
                  :rules="bicRules"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                class="ml-0 pl-0"
                v-if="canShow(scanFormat)"
              >
                <v-text-field
                  label="Account Number"
                  v-model="accNumber"
                  :rules="accNumberRules"
                  required
                />
                <v-text-field
                  label="Sort Code"
                  v-model="sortCode"
                  :rules="sortCodeRules"
                  required
                />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="text-btn mr-2"
            text
            @click="close()"
          >
            Cancel
          </v-btn>
          <v-btn
            v-if="isEdit && valid"
            class="text-btn mr-2"
            text
            @click="update()"
          >
            Update
          </v-btn>
          <v-btn
            v-if="!isEdit && valid"
            class="text-btn mr-2"
            text
            @click="create()"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import * as R from 'ramda'
import { currencies } from 'src/constants'
import validator from 'validator'
import {
  createCompanyBankAccount,
  updateBankAccount
} from '../../modules/companies/apiCalls'
import { createAndUpdatePartnerBankDetail } from '../../modules/partners/apiCalls'

const SCAN_FORMAT = 'SCAN'
const IBAN_FORMAT = 'IBAN'
const EUR = 'EUR'
const GBP = 'GBP'
const isDefined = R.complement(R.converge(R.or, [R.isNil, R.isEmpty]))

export default {
  name: 'BankAccountDialog',
  props: {
    value: { type: Boolean, required: true },
    companyID: { type: String, required: true, default: '' },
    accounts: { type: Array, default: () => [] },
    selectedAccount: { type: Object, default: () => {} },
    isEdit: { type: Boolean, default: false },
    createEUR: { type: Boolean, default: false },
    createGBP: { type: Boolean, default: false },
    companyName: { type: String, default: () => '' }
  },
  data() {
    return {
      valid: false,
      currency: EUR,
      bic: '',
      bicRules: [
        (v) => !!v || 'BIC is required',
        (v) => validator.isBIC(v) || 'Invalid BIC'
      ],
      iban: '',
      ibanRules: [
        (v) => !!v || 'IBAN is required',
        (v) => validator.isIBAN(v) || 'Invalid IBAN'
      ],
      sortCode: '',
      sortCodeRules: [
        (v) => !!v || 'Sort Code is required',
        (v) => R.length(v) === 6 || 'Invalid Sort Code'
      ],
      accNumber: '',
      accNumberRules: [
        (v) => !!v || 'Account Number is required',
        (v) => R.length(v) === 8 || 'Invalid Account Number'
      ],
      selectedFormat: 'IBAN',
      scanFormat: SCAN_FORMAT,
      ibanFormat: IBAN_FORMAT,
      currencyEUR: EUR,
      currencyGBP: GBP
    }
  },
  computed: {
    selectedIsEUR() {
      return this.selectedAccount.currency === EUR
    },
    selectedIsGBP() {
      return this.selectedAccount.currency === GBP
    },
    getCurrency() {
      if (this.isEdit) {
        if (this.selectedIsEUR) {
          this.selectedFormat = IBAN_FORMAT
          return EUR
        }
        if (this.selectedIsGBP) {
          this.selectedFormat = SCAN_FORMAT
          return GBP
        }
      }
      if (this.createEUR) {
        this.selectedFormat = IBAN_FORMAT
        return EUR
      }
      if (this.createGBP) {
        this.selectedFormat = SCAN_FORMAT
        return GBP
      }
    },
    emptyAccounts() {
      return R.isEmpty(this.accounts)
    },
    reqObj() {
      const req = {
        name: this.companyName,
        iban: this.iban,
        bic: this.bic,
        sort_code: this.sortCode,
        account_number: this.accNumber
      }
      if (this.isEdit) {
        delete req.name
      }
      return this.cleanReqObj(req)
    },
    reqUpdatePartnerEURBankAccount() {
      if (this.isPartner)
        return {
          partner_id: this.$route.params.partnerId,
          currency: this.currency,
          iban: {
            iban: this.iban,
            bic: this.bic
          }
        }
    },
    reqUpdatePartnerGBPBankAccount() {
      if (this.isPartner)
        return {
          partner_id: this.$route.params.partnerId,
          currency: this.currency,
          scan: {
            account_number: this.accNumber,
            sort_code: this.sortCode
          }
        }
    },
    isPartner() {
      return this.$route.name === 'partner-details'
    }
  },
  methods: {
    canShow(format) {
      return this.selectedFormat === format
    },
    cleanReqObj(o) {
      return R.reject((key) => !isDefined(key), R.clone(o))
    },
    cleanInputs() {
      this.bic = ''
      this.iban = ''
      this.sortCode = ''
      this.accNumber = ''
    },
    async updateOrCreatePartnerBankAccount() {
      const payload =
        this.currency === 'EUR'
          ? this.reqUpdatePartnerEURBankAccount
          : this.reqUpdatePartnerGBPBankAccount

      try {
        await createAndUpdatePartnerBankDetail(payload)
      } catch (e) {
        console.error(e)
        this.notifyError({
          message:
            'There was a problem with bank accounts. Contact with Tech team.'
        })
      }
    },
    async update() {
      this.loading = true
      try {
        if (!this.isPartner) {
          await updateBankAccount(
            this.companyID,
            this.selectedAccount.id,
            this.reqObj
          )
        } else {
          await this.updateOrCreatePartnerBankAccount()
        }
      } catch (e) {
        console.error(e)
        this.notifyError({
          message:
            'There was a problem with bank accounts. Contact with Tech team.'
        })
      }
      this.close(true)
    },
    async create() {
      this.loading = true
      try {
        if (!this.isPartner) {
          await createCompanyBankAccount(this.companyID, this.reqObj)
        } else {
          await this.updateOrCreatePartnerBankAccount()
        }
      } catch (e) {
        console.error(e)
        this.notifyError({
          message:
            'There was a problem with bank accounts. Contact with Tech team.'
        })
      }
      this.close(true)
    },
    close(requestMade) {
      this.$emit('dialog-closed', requestMade)
      this.$refs.form.resetValidation()
      this.cleanInputs()
    }
  },
  created() {
    this.currencies = currencies
  },
  watch: {
    currency(v) {
      v === EUR
        ? (this.selectedFormat = IBAN_FORMAT)
        : (this.selectedFormat = SCAN_FORMAT)
      this.cleanInputs()
    },
    selectedFormat() {
      this.$refs.form.resetValidation()
      this.cleanInputs()
    },
    value(v) {
      if (this.isEdit) {
        this.$nextTick(() => {
          if (this.selectedIsEUR) {
            this.iban = this.selectedAccount.iban.iban
              ? this.selectedAccount.iban.iban
              : this.selectedAccount.iban
            this.bic = this.selectedAccount.iban.bic
              ? this.selectedAccount.iban.bic
              : this.selectedAccount.bic
          }
          if (this.selectedIsGBP) {
            this.sortCode = this.selectedAccount.scan
              ? this.selectedAccount.scan.sort_code
              : this.selectedAccount.sort_code
            this.accNumber = this.selectedAccount.scan
              ? this.selectedAccount.scan.account_number
              : this.selectedAccount.account_number
          }
        })
      }
    }
  }
}
</script>

<style>
.v-subheader {
  justify-content: space-around;
}
.acc-currency-input > .v-input__control > .v-input__slot:before {
  border-style: none;
}
.acc-currency-input > .v-input__control > .v-input__slot:after {
  border-style: none;
}
</style>
